// ─── Base MetricRecord ────────────────────────────────────────────────────────
import { MetricRecord } from "@/metrics/runtime"

import { logger } from "@/lib/logger"
import type { MetricRecordData } from "@/types/metrics/contracts"

import {
  AGENT_JUDGE_CATEGORICAL_MANAGER,
  AGENT_JUDGE_MANAGER,
  AGENT_JUDGE_NUMERICAL_MANAGER,
} from "../configuration/optimization/constants"
import { ApiStateMetricRecord } from "../records/ApiStateMetricRecord"
import { CategoricalMetricRecord } from "../records/base/CategoricalMetricRecord"
// ─── Type Bases ───────────────────────────────────────────────────────────────
import { NumericalMetricRecord } from "../records/base/NumericalMetricRecord"
import { SetMetricRecord } from "../records/base/SetMetricRecord"
// ─── Binary ───────────────────────────────────────────────────────────────────
import { AgentFailsToRespondMetricRecord } from "../records/binary/AgentFailsToRespondMetricRecord"
import { AgentJudgeMetricRecord } from "../records/binary/AgentJudgeMetricRecord"
import { AgentNeedsRepromptingMetricRecord } from "../records/binary/AgentNeedsRepromptingMetricRecord"
import { AgentRefusalMetricRecord } from "../records/binary/AgentRefusalMetricRecord"
import { AgentRepeatsItselfMetricRecord } from "../records/binary/AgentRepeatsItselfMetricRecord"
import { AudioBinaryLLMMetricRecord } from "../records/binary/AudioBinaryLLMMetricRecord"
import { BinaryLLMMetricRecord } from "../records/binary/BinaryLLMMetricRecord"
// ─── Custom Configurable Variants ────────────────────────────────────────────
import { CustomAgentFailsToRespondMetricRecord } from "../records/binary/CustomAgentFailsToRespondMetricRecord"
import { CustomAgentNeedsRepromptingMetricRecord } from "../records/binary/CustomAgentNeedsRepromptingMetricRecord"
import { CustomAudioSentimentMetricRecord } from "../records/binary/CustomAudioSentimentMetricRecord"
import { CustomEndReasonMetricRecord } from "../records/binary/CustomEndReasonMetricRecord"
import { TranscriptRegexMatchMetricRecord } from "../records/binary/TranscriptRegexMatchMetricRecord"
import { UserFrustrationMetricRecord } from "../records/binary/UserFrustrationMetricRecord"
import { WordsPerMessageWithThresholdMetricRecord } from "../records/binary/WordsPerMessageWithThresholdMetricRecord"
import { AgentJudgeCategoricalMetricRecord } from "../records/categorical/AgentJudgeCategoricalMetricRecord"
// ─── Categorical ──────────────────────────────────────────────────────────────
import { AudioCategoricalLLMMetricRecord } from "../records/categorical/AudioCategoricalLLMMetricRecord"
// ─── Categorical (audio) ─────────────────────────────────────────────────────
import { AudioSentimentMetricRecord } from "../records/categorical/AudioSentimentMetricRecord"
import { CategoricalLLMMetricRecord } from "../records/categorical/CategoricalLLMMetricRecord"
import { DerivedStrictLogicMetricRecord } from "../records/categorical/DerivedStrictLogicMetricRecord"
import { EndReasonMetricRecord } from "../records/categorical/EndReasonMetricRecord"
import { MatchExpectedSimWrapperMetricRecord } from "../records/categorical/MatchExpectedSimWrapperMetricRecord"
import { SentimentAnalysisMetricRecord } from "../records/categorical/SentimentAnalysisMetricRecord"
import { CompositeEvaluationMetricRecord } from "../records/CompositeEvaluationMetricRecord"
import { LLMTextMetricRecord } from "../records/LLMTextMetricRecord"
import { MetadataFieldMetricMetricRecord } from "../records/MetadataFieldMetricMetricRecord"
import { AbruptPitchChangesMetricRecord } from "../records/numerical/AbruptPitchChangesMetricRecord"
import { AgentJudgeNumericalMetricRecord } from "../records/numerical/AgentJudgeNumericalMetricRecord"
import { AudioDurationMetricRecord } from "../records/numerical/AudioDurationMetricRecord"
import { AudioFrequencyMetricRecord } from "../records/numerical/AudioFrequencyMetricRecord"
// ─── Numerical ────────────────────────────────────────────────────────────────
import { AudioNumericalLLMMetricRecord } from "../records/numerical/AudioNumericalLLMMetricRecord"
import { ClippingArtifactMetricRecord } from "../records/numerical/ClippingArtifactMetricRecord"
import { CodecArtifactMetricRecord } from "../records/numerical/CodecArtifactMetricRecord"
import { CustomAudioFrequencyMetricRecord } from "../records/numerical/CustomAudioFrequencyMetricRecord"
import { CustomTraceMetricRecord } from "../records/numerical/CustomTraceMetricRecord"
import { DerivedAggregateMetricRecord } from "../records/numerical/DerivedAggregateMetricRecord"
import { DropoutArtifactMetricRecord } from "../records/numerical/DropoutArtifactMetricRecord"
import { InterruptionRateMetricRecord } from "../records/numerical/InterruptionRate"
import { IvrFlowAdherenceMetricRecord } from "../records/numerical/IvrFlowAdherenceMetricRecord"
import { LatencyMetricRecord } from "../records/numerical/LatencyMetricRecord"
import { LoopDetectionMetricRecord } from "../records/numerical/LoopDetectionMetricRecord"
import { LufsMetricRecord } from "../records/numerical/LufsMetricRecord"
import { MusicDetectionMetricRecord } from "../records/numerical/MusicDetectionMetricRecord"
import { NonExpressivePausesMetricRecord } from "../records/numerical/NonExpressivePausesMetricRecord"
import { NumericalLLMMetricRecord } from "../records/numerical/NumericalLLMMetricRecord"
import { PauseAnalysisMetricRecord } from "../records/numerical/PauseAnalysisMetricRecord"
import { PhonemeStretchMetricRecord } from "../records/numerical/PhonemeStretchMetricRecord"
import { PitchVariabilityMetricRecord } from "../records/numerical/PitchVariabilityMetricRecord"
import { RubricDecisionTreeMetricRecord } from "../records/numerical/RubricDecisionTreeMetricRecord"
import { SignalToNoiseMetricRecord } from "../records/numerical/SignalToNoiseMetricRecord"
import { SpeakingTimePercentageMetricRecord } from "../records/numerical/SpeakingTimePercentageMetricRecord"
import { SpectrogramPitchAnalysisMetricRecord } from "../records/numerical/SpectrogramPitchAnalysisMetricRecord"
import { SpeechArtifactAnomalyMetricRecord } from "../records/numerical/SpeechArtifactAnomalyMetricRecord"
import { SpeechTempoMetricRecord } from "../records/numerical/SpeechTempoMetricRecord"
import { SqlFloatMetricRecord } from "../records/numerical/SqlMetricRecord"
import { SyllableRateMetricRecord } from "../records/numerical/SyllableRateMetricRecord"
import { TerminalContourMetricRecord } from "../records/numerical/TerminalContourMetricRecord"
import { TimbreDriftMetricRecord } from "../records/numerical/TimbreDriftMetricRecord"
import { TimeToFirstAudioMetricRecord } from "../records/numerical/TimeToFirstAudioMetricRecord"
import { TranscriptionErrorMetricRecord } from "../records/numerical/TranscriptionErrorMetricRecord"
import { VocalFryManagerMetricRecord } from "../records/numerical/VocalFryMetricRecord"
import { VoiceQualityMetricRecord } from "../records/numerical/VoiceQualityMetricRecord"
import { VolumePitchMisalignmentMetricRecord } from "../records/numerical/VolumePitchMisalignmentMetricRecord"
import { VolumeVarianceMetricRecord } from "../records/numerical/VolumeVarianceMetricRecord"
import { WordsPerMessageMetricRecord } from "../records/numerical/WordsPerMessageMetricRecord"

const log = logger("createMetricInstance")

export const createMetricInstance = (data: MetricRecordData): MetricRecord => {
  switch (data.metric_manager) {
    // ── Binary ──────────────────────────────────────────────────────────────
    case AGENT_JUDGE_MANAGER:
      return new AgentJudgeMetricRecord(data)
    case "manager_llm_binary":
      return new BinaryLLMMetricRecord(data)
    case "manager_audio_llm_binary":
      return new AudioBinaryLLMMetricRecord(data)
    case "manager_agent_fails_to_respond":
      return new AgentFailsToRespondMetricRecord(data)
    case "manager_agent_needs_reprompting":
      return new AgentNeedsRepromptingMetricRecord(data)
    case "manager_agent_repeats_itself":
      return new AgentRepeatsItselfMetricRecord(data)
    case "manager_transcript_regex_match":
      return new TranscriptRegexMatchMetricRecord(data)
    case "manager_user_frustration":
      return new UserFrustrationMetricRecord(data)
    case "manager_agent_refusal":
      return new AgentRefusalMetricRecord(data)
    case "manager_words_per_message_with_threshold":
      return new WordsPerMessageWithThresholdMetricRecord(data)

    // ── Categorical ─────────────────────────────────────────────────────────
    case "manager_audio_llm_categorical":
      return new AudioCategoricalLLMMetricRecord(data)
    case "manager_default_categorical":
      return new CategoricalLLMMetricRecord(data)
    case AGENT_JUDGE_CATEGORICAL_MANAGER:
      return new AgentJudgeCategoricalMetricRecord(data)
    case "manager_derived_strict_logic":
      return new DerivedStrictLogicMetricRecord(data)
    case "manager_end_reason":
      return new EndReasonMetricRecord(data)
    case "manager_llm_text":
      return new LLMTextMetricRecord(data)
    case "manager_sentimentanalysis":
      return new SentimentAnalysisMetricRecord(data)
    case "manager_match_expected_sim_wrapper":
      return new MatchExpectedSimWrapperMetricRecord(data)

    // ── Numerical ───────────────────────────────────────────────────────────
    case "manager_audio_llm_numerical":
      return new AudioNumericalLLMMetricRecord(data)
    case "interruption_rate_manager":
      return new InterruptionRateMetricRecord(data)
    case "manager_persona_interruption_rate":
      return new InterruptionRateMetricRecord(data)
    case "manager_latency":
      return new LatencyMetricRecord(data)
    case "manager_persona_latency":
      return new LatencyMetricRecord(data)
    case "manager_numerical_llm_judge":
      return new NumericalLLMMetricRecord(data)
    case AGENT_JUDGE_NUMERICAL_MANAGER:
      return new AgentJudgeNumericalMetricRecord(data)
    case "manager_signal_to_noise_ratio":
      return new SignalToNoiseMetricRecord(data)
    case "manager_time_to_first_audio":
      return new TimeToFirstAudioMetricRecord(data)
    case "manager_transcription_error":
      return new TranscriptionErrorMetricRecord(data)
    case "manager_words_per_message":
      return new WordsPerMessageMetricRecord(data)
    case "manager_abrupt_pitch_changes":
      return new AbruptPitchChangesMetricRecord(data)
    case "manager_pitch_variability":
      return new PitchVariabilityMetricRecord(data)
    case "manager_lufs":
      return new LufsMetricRecord(data)
    case "manager_terminal_contour":
      return new TerminalContourMetricRecord(data)
    case "audio_duration_metric_manager":
      return new AudioDurationMetricRecord(data)
    case "manager_audio_frequency":
      return new AudioFrequencyMetricRecord(data)
    case "manager_clipping_artifact":
      return new ClippingArtifactMetricRecord(data)
    case "manager_codec_artifact":
      return new CodecArtifactMetricRecord(data)
    case "manager_persona_clipping_artifact":
      return new ClippingArtifactMetricRecord(data)
    case "manager_persona_codec_artifact":
      return new CodecArtifactMetricRecord(data)
    case "manager_dropout_artifact":
      return new DropoutArtifactMetricRecord(data)
    case "manager_non_expressive_pauses":
      return new NonExpressivePausesMetricRecord(data)
    case "manager_persona_non_expressive_pauses":
      return new NonExpressivePausesMetricRecord(data)
    case "pause_analysis_metric":
      return new PauseAnalysisMetricRecord(data)
    case "manager_phonemes_per_second":
      return new SpeechTempoMetricRecord(data)
    case "manager_phoneme_stretch":
      return new PhonemeStretchMetricRecord(data)
    case "manager_persona_phoneme_stretch":
      return new PhonemeStretchMetricRecord(data)
    case "manager_spectrogram_pitch_analysis":
      return new SpectrogramPitchAnalysisMetricRecord(data)
    case "manager_vocal_fry":
      return new VocalFryManagerMetricRecord(data)
    case "manager_volume_pitch_misalignment":
      return new VolumePitchMisalignmentMetricRecord(data)
    case "manager_volume":
      return new VolumeVarianceMetricRecord(data)
    case "manager_loop_detection":
      return new LoopDetectionMetricRecord(data)
    case "manager_music_detection":
      return new MusicDetectionMetricRecord(data)
    case "manager_persona_volume_pitch_misalignment":
      return new VolumePitchMisalignmentMetricRecord(data)
    case "manager_persona_volume":
      return new VolumeVarianceMetricRecord(data)
    case "manager_persona_loop_detection":
      return new LoopDetectionMetricRecord(data)
    case "manager_persona_music_detection":
      return new MusicDetectionMetricRecord(data)
    case "manager_syllable_rate":
      return new SyllableRateMetricRecord(data)
    case "manager_voice_quality":
      return new VoiceQualityMetricRecord(data)
    case "manager_timbre_drift":
      return new TimbreDriftMetricRecord(data)
    case "manager_speech_artifact_anomaly":
      return new SpeechArtifactAnomalyMetricRecord(data)
    case "manager_speaking_time_percentage":
      return new SpeakingTimePercentageMetricRecord(data)

    // ── Categorical (audio) ────────────────────────────────────────────────
    case "manager_audio_sentiment":
      return new AudioSentimentMetricRecord(data)

    // ── Base MetricRecord ───────────────────────────────────────────────────
    case "manager_api_state":
      return new ApiStateMetricRecord(data)
    case "manager_composite_evaluation":
      return new CompositeEvaluationMetricRecord(data)
    case "manager_metadata_field_metric":
      return new MetadataFieldMetricMetricRecord(data)
    case "manager_custom_trace":
      return new CustomTraceMetricRecord(data)
    case "manager_sql_float":
      return new SqlFloatMetricRecord(data)
    case "manager_derived_aggregate":
      return new DerivedAggregateMetricRecord(data)
    case "manager_rubric_decision_tree":
      return new RubricDecisionTreeMetricRecord(data)
    case "manager_ivr_flow_adherence":
      return new IvrFlowAdherenceMetricRecord(data)
    case "manager_custom_audio_frequency":
      return new CustomAudioFrequencyMetricRecord(data)
    case "manager_custom_end_reason":
      return new CustomEndReasonMetricRecord(data)
    case "manager_custom_agent_needs_reprompting":
      return new CustomAgentNeedsRepromptingMetricRecord(data)
    case "manager_custom_audio_sentiment":
      return new CustomAudioSentimentMetricRecord(data)
    case "manager_custom_agent_fails_to_respond":
      return new CustomAgentFailsToRespondMetricRecord(data)

    default:
      if (data.output_type === "float") {
        return new NumericalMetricRecord(data)
      }
      if (data.output_type === "string") {
        return new CategoricalMetricRecord(data)
      }
      if (data.output_type === "set") {
        return new SetMetricRecord(data)
      }
      log.warn(
        "Falling back to base MetricRecord for unrecognized output_type; compareValues will throw if called",
        {
          output_type: data.output_type,
          metric_manager: data.metric_manager,
          id: data.id,
        },
      )
      return new MetricRecord(data)
  }
}

export const convertResponseToMetricRecord = (
  response: unknown,
): MetricRecord => {
  const dataShape =
    typeof response === "object" && response !== null
      ? (response as Partial<MetricRecordData> & {
          organization_id?: string | null
        })
      : {}
  const data: MetricRecordData = {
    id: dataShape.id,
    metric_name: dataShape.metric_name ?? "",
    metric_display_name: dataShape.metric_display_name ?? "",
    metric_metadata: dataShape.metric_metadata ?? {},
    description: dataShape.description ?? "",
    organization_id: dataShape.organization_id ?? undefined,
    metric_manager: dataShape.metric_manager ?? "",
    locked:
      dataShape.organization_id === null || dataShape.organization_id === "",
    category: dataShape.category,
    created_at: dataShape.created_at,
    created_by: dataShape.created_by,
    last_updated_at: dataShape.last_updated_at,
    last_updated_by: dataShape.last_updated_by,
    output_type: dataShape.output_type,
    organization_threshold: dataShape.organization_threshold ?? null,
    tags: dataShape.tags ?? [],
    current_version_id: dataShape.current_version_id ?? null,
    current_version_number: dataShape.current_version_number ?? null,
  }
  return createMetricInstance(data)
}
